import { Modal } from 'antd-mobile'
import qs from 'query-string'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as actions from '../../actions/orderActions'
import Button from '../../components/elements/Button'
import { ORDER_METHOD, PARAMS_NAME, PAYMENT_METHOD } from '../../constants'
import useMoney from '../../hooks/useMoney'
import useOrder from '../../hooks/useOrder'
import useOrderWaitPayment from '../../hooks/useOrderWaitPayment'
import useTimeWork from '../../hooks/useTimeWork'
import {
    ensureObject,
    errorHandle,
    getBodyScrollTop,
    showMessageSuccess,
    showMessageWarn,
} from '../../utils/helper'
import {
    calcMoneyFoodList,
    checkOverTime,
    makeAddressCalcDistance,
} from '../../utils/index'
import ModalPayment from '../ModalPayment'
import CustomerInfoContainer from './CustomerInfoContainer'
import RestaurantInfoContainer from './RestaurantInfoContainer'

const alertAnt = Modal.alert

const ENV = process.env.NODE_ENV === 'production' ? 'production' : 'sandbox'

const OrderFormContainer = ({ backToOrder }) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const { t } = useTranslation()
    const { isOvertime } = useTimeWork()
    const { ordering, orderMethod, deliverPosition } = useSelector(
        state => state.OrderReducer
    )
    const { paypalClientId, paypalCurrentcyCode } = useSelector(
        state => state.RestaurantReducer
    )
    const { validShippingDistance, shippingDistanceMessage } = useOrder();

    const {
        showPaymentPaypal,
        setShowPaymentPaypal,
        orderSuccessData,
        setOrderSuccessData,
    } = useOrderWaitPayment()

    const userInfoRef = useRef()
    const [voucherVerified, setVoucherVerified] = useState(false)

    const { total: totalOrdering, originTotal: originTotalOrdering } = useMoney(
        calcMoneyFoodList,
        ordering
    )

    const orderAtRestaurant = () => {
        dispatch(
            actions.submitOrder({
                params: {},
                onDone: ({ success, responseData }) => {
                    if (success && responseData.result) {
                        handleGetOrdersQRCode()

                        showMessageSuccess(
                            t('modalOrderSuccess.mesSubmitOrderSuccess')
                        )
                    }
                },
            })
        )
    }

    const handleShowOrderStatus = data => {
        history.replace({
            pathname: history.location.pathname,
            search: qs.stringify({
                [PARAMS_NAME.ORDER_WEB_ID]:
                    data?.tableNumber || orderSuccessData?.tableNumber,
            }),
        })
    }

    const handleSubmitOrder = async () => {
        if (checkOverTime()) {
            return alertAnt('', t('modalOverTime.message'), [
                { text: t('button.agree') },
            ])
        }

        if (orderMethod === ORDER_METHOD.QR_CODE) {
            return orderAtRestaurant()
        }

        const customer = await userInfoRef.current.validateForm()
        if (!customer) {
            return
        }

        if (!validShippingDistance) {
            return showMessageWarn(shippingDistanceMessage);
        }

        const voucherState = userInfoRef.current?.voucherState
        if (voucherState?.code?.trim() && !voucherState?.verified) {
            return showMessageWarn(t('voucher.invalidVoucher'))
        }

        const voucherId = voucherState?.id

        dispatch(
            actions.saveOrder({
                params: {
                    customer,
                    voucherId,
                },
                onDone: ({ success, responseData }) => {
                    console.log({ success, responseData })

                    if (!success || !responseData.result) {
                        return errorHandle({
                            message: 'apiMessage.saveOrderFailed',
                        })
                    }

                    const { data } = responseData || {}
                    setOrderSuccessData(data)

                    if (customer.paymentMethod === PAYMENT_METHOD.PAYPAL) {
                        history.replace({
                            pathname: history.location.pathname,
                            search: qs.stringify({
                                [PARAMS_NAME.ORDER_WAIT_PAYMENT]:
                                    data?.tableNumber ||
                                    orderSuccessData?.tableNumber,
                            }),
                        })
                        setShowPaymentPaypal(true)
                    } else {
                        handleShowOrderStatus(data)
                    }
                },
            })
        )
    }

    const handleApprove = (data, actions) => {
        setShowPaymentPaypal(false)
        handleShowOrderStatus()
    }

    const handleCalcDistance = desc => {
        if (orderMethod !== ORDER_METHOD.DELIVERY) {
            return
        }

        let destinations = desc
        if (!destinations) {
            const customer = userInfoRef.current.getUserAddress()

            const { userAddressNumber, userStreet, userCity, userPostcode } = ensureObject(customer);
            console.log({ userPostcode });
            destinations = makeAddressCalcDistance(userStreet, userAddressNumber, userCity, userPostcode);
        }

        console.log('desc', { destinations })

        dispatch(
            actions.calcDistance({
                params: {
                    destinations,
                },
                onError: () => {
                    userInfoRef.current.setErrors(prev => ({
                        ...prev,
                        userStreet: 'error',
                        userAddressNumber: 'error',
                        userCity: 'error',
                        userPostcode: 'error',
                    }));
                },
                onDone: () => {
                    userInfoRef.current.setErrors(prev => ({
                        ...prev,
                        userStreet: '',
                        userAddressNumber: '',
                        userCity: '',
                        userPostcode: '',
                    }));
                },
            })
        )
    }

    const checkValidAddress = () => {
        const { userAddressNumber, userStreet, userCity } =
            ensureObject(deliverPosition)

        return userAddressNumber && userStreet && userCity
    }

    const handleGetOrdersQRCode = () => {
        dispatch(
            actions.getOrdersQRCode({
                params: {},
            })
        )
    }

    const handlePayOther = payMethod => {
        dispatch(
            actions.payOther({
                params: {
                    orderID: orderSuccessData?.id,
                    payMethod,
                },
                onDone: ({ success, responseData }) => {
                    if (success) {
                        setShowPaymentPaypal(false)
                        handleShowOrderStatus()
                    }
                },
            })
        )
    }

    const validAddress = checkValidAddress()

    const voucherState = userInfoRef.current?.voucherState
    const discountCodeEntered = voucherState?.code?.trim()
    const voucherNotVerified = discountCodeEntered && !voucherState?.verified

    const disableButtonOrder =
        isOvertime ||
        !ordering.length ||
        !validShippingDistance ||
        (orderMethod !== ORDER_METHOD.QR_CODE && !validAddress) ||
        voucherNotVerified

    console.log(
        isOvertime,
        !ordering.length,
        !validShippingDistance,
        orderMethod !== ORDER_METHOD.QR_CODE && !validAddress
    )

    useEffect(() => {
        orderMethod === ORDER_METHOD.QR_CODE && handleGetOrdersQRCode()
    }, [])

    useEffect(() => {
        if (orderMethod === ORDER_METHOD.DELIVERY && validAddress) {
            console.log('orderMethod change => handleCalcDistance()')
            handleCalcDistance()
        }
    }, [orderMethod])

    useEffect(() => {
        setTimeout(() => {
            getBodyScrollTop().scrollTop = 0;
        });
    }, [])

    return (
        <div className="food-desk-form">
            <RestaurantInfoContainer backToList={backToOrder} />

            <div className="container-fluid container-xl">
                <CustomerInfoContainer
                    handleCalcDistance={handleCalcDistance}
                    ref={userInfoRef}
                    onVoucherStateChange={setVoucherVerified}
                />
            </div>

            <div className="container-fluid container-xl">
                <div className="food-desk-form-group">
                    <div className="box-form pt-8">
                        <div className="list-action">
                            <Button
                                disabled={disableButtonOrder}
                                onClick={handleSubmitOrder}
                                large
                                style={{ fontSize: '16px' }}
                            >
                                {t('cartDesktop.btnSubmitOrder')}
                            </Button>
                            <div className="policy-note">
                                {t('cartDesktop.lbAgreeTCPre')}{' '}
                                <span className="highlight">
                                    {t('cartDesktop.lbTermAndCondition')}
                                </span>{' '}
                                {t('cartDesktop.lbAgreeTCRest')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal payment method */}
            <ModalPayment
                showPaymentPaypal={showPaymentPaypal}
                orderSuccessData={orderSuccessData}
                paypalClientId={paypalClientId}
                handleApprove={handleApprove}
                paypalCurrentcyCode={paypalCurrentcyCode}
                handlePayOther={handlePayOther}
            />
        </div>
    )
}

export default OrderFormContainer
